/**
 * Configuration management (Phase C).
 *
 * - "Pull snapshot" → POST /config/get/:chargerId
 * - Drift badge   → GET /config/drift/:chargerId
 * - History list  → GET /config/history/:chargerId
 * - Edit drawer   → POST /config/set/:chargerId  (one or more keys)
 *
 * For OCPP 2.0.1 the backend translates to GetVariables / SetVariables.
 * The frontend cares only about `{ key, value, readonly }` either way.
 */

import React, { useCallback, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import OcppActionButton from '../components/OcppActionButton';
import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import {
  getConfig,
  getConfigHistory,
  getDriftReport,
  setConfig,
} from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { formatOcppTimestamp } from '../utils/statusFormat';
import type {
  ConfigChangeRow,
  ConfigKey,
  ConfigSetStatus,
  DriftEntry,
  GetConfigHistoryResponse,
  GetConfigResponse,
  GetDriftResponse,
  SetConfigResponse,
} from '../models';
import type { OcppTabProps } from './types';

const STATUS_COLOR: Record<ConfigSetStatus | 'Unknown', string> = {
  Accepted: OcppTheme.success,
  Rejected: OcppTheme.danger,
  RebootRequired: OcppTheme.warning,
  NotSupported: OcppTheme.textMuted,
  Unknown: OcppTheme.textMuted,
};

const ConfigurationTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [snapshot, setSnapshot] = useState<GetConfigResponse | null>(null);
  const [drift, setDrift] = useState<GetDriftResponse | null>(null);
  const [history, setHistory] = useState<ConfigChangeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [pulling, setPulling] = useState(false);
  const [editingKey, setEditingKey] = useState<ConfigKey | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [cfg, dft, hist] = await Promise.all([
      getConfig(chargerId),
      getDriftReport(chargerId),
      getConfigHistory(chargerId, { limit: 50 }),
    ]);
    if (isOcppSuccess(cfg.status)) {
      setSnapshot(cfg.payload as GetConfigResponse | null);
      setError(null);
    } else {
      setError(mapOcppApiError(cfg.status, cfg.payload));
    }
    if (isOcppSuccess(dft.status)) {
      setDrift(dft.payload as GetDriftResponse | null);
    }
    if (isOcppSuccess(hist.status)) {
      const payload = hist.payload as GetConfigHistoryResponse | null;
      setHistory(payload?.changes ?? []);
    }
    setLoading(false);
  }, [chargerId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onPull = useCallback(async () => {
    setPulling(true);
    const res = await getConfig(chargerId);
    if (isOcppSuccess(res.status)) {
      setSnapshot(res.payload as GetConfigResponse | null);
      setError(null);
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setPulling(false);
  }, [chargerId]);

  const driftCount = drift?.report?.drift?.length ?? 0;

  if (loading) return <OcppLoadingState label="Loading configuration…" />;
  if (error && !snapshot) return <OcppErrorBanner error={error} onRetry={load} />;

  return (
    <View>
      {error ? <OcppErrorBanner error={error} onRetry={load} /> : null}

      <OcppSectionCard
        title="Drift"
        subtitle={
          drift && drift.report
            ? driftCount === 0
              ? 'Charger matches the assigned baseline.'
              : `${driftCount} key(s) differ from the assigned baseline.`
            : 'No baseline assigned to this charger.'
        }
        trailing={
          drift && drift.report ? (
            <OcppStatusPill
              label={driftCount === 0 ? 'In sync' : `${driftCount} drift`}
              color={driftCount === 0 ? OcppTheme.success : OcppTheme.warning}
            />
          ) : null
        }
      >
        {driftCount === 0 ? null : <DriftList drift={drift!.report.drift} />}
      </OcppSectionCard>

      <OcppSectionCard
        title="Configuration"
        subtitle={
          snapshot?.pulled_at
            ? `Last pulled ${formatOcppTimestamp(snapshot.pulled_at)}`
            : 'Pull a snapshot to see the live key/value set.'
        }
        trailing={
          <OcppActionButton
            label={pulling ? 'Pulling…' : 'Pull snapshot'}
            icon="refresh"
            onPress={onPull}
            loading={pulling}
            variant="secondary"
          />
        }
      >
        {snapshot && snapshot.keys && snapshot.keys.length > 0 ? (
          snapshot.keys.map((k) => (
            <ConfigKeyRow
              key={k.key}
              entry={k}
              onEdit={k.readonly ? undefined : () => setEditingKey(k)}
            />
          ))
        ) : (
          <OcppEmptyState icon="tune" title="No keys yet" description="Pull a snapshot to populate this view." />
        )}
      </OcppSectionCard>

      <OcppSectionCard title="History" subtitle="Last 50 per-key changes (snapshot diffs and explicit sets).">
        {history.length === 0 ? (
          <OcppEmptyState icon="history" title="No changes recorded" />
        ) : (
          history.map((row) => (
            <View key={row.id} style={styles.historyRow}>
              <View style={styles.historyHeader}>
                <Text style={styles.historyKey}>{row.config_key}</Text>
                <Text style={styles.historyResult}>{row.result}</Text>
              </View>
              <Text style={styles.historyValue}>
                {(row.old_value ?? '—') + ' → ' + (row.new_value ?? '—')}
              </Text>
              <Text style={styles.historyMeta}>
                {formatOcppTimestamp(row.createdAt)}
                {row.reason ? ` · ${row.reason}` : ''}
              </Text>
            </View>
          ))
        )}
      </OcppSectionCard>

      <Modal
        visible={editingKey !== null}
        animationType="slide"
        onRequestClose={() => setEditingKey(null)}
      >
        {editingKey ? (
          <EditKeyDrawer
            chargerId={chargerId}
            entry={editingKey}
            onClose={() => setEditingKey(null)}
            onSaved={async () => {
              setEditingKey(null);
              await load();
            }}
          />
        ) : null}
      </Modal>
    </View>
  );
};

export default ConfigurationTab;

const DriftList: React.FC<{ drift: DriftEntry[] }> = ({ drift }) => (
  <View style={{ gap: 8 }}>
    {drift.map((d) => (
      <View key={d.key} style={styles.driftRow}>
        <Text style={styles.driftKey}>{d.key}</Text>
        <Text style={styles.driftValue}>
          expected <Text style={styles.driftMono}>{d.expected ?? '—'}</Text>
          {' · actual '}
          <Text style={styles.driftMono}>{d.actual ?? '—'}</Text>
        </Text>
      </View>
    ))}
  </View>
);

const ConfigKeyRow: React.FC<{ entry: ConfigKey; onEdit?: () => void }> = ({ entry, onEdit }) => (
  <View style={styles.cfgRow}>
    <View style={{ flex: 1 }}>
      <Text style={styles.cfgKey}>{entry.key}</Text>
      <Text style={styles.cfgValue} selectable>
        {entry.value ?? '—'}
      </Text>
    </View>
    {entry.readonly ? (
      <OcppStatusPill label="readonly" color={OcppTheme.textMuted} size="sm" />
    ) : onEdit ? (
      <Pressable
        onPress={onEdit}
        accessibilityRole="button"
        accessibilityLabel={`Edit ${entry.key}`}
        style={({ pressed }) => [styles.editBtn, pressed && { opacity: 0.7 }]}
      >
        <MaterialIcons name="edit" size={18} color={OcppTheme.accent} />
      </Pressable>
    ) : null}
  </View>
);

const EditKeyDrawer: React.FC<{
  chargerId: string;
  entry: ConfigKey;
  onClose: () => void;
  onSaved: () => void;
}> = ({ chargerId, entry, onClose, onSaved }) => {
  const [value, setValue] = useState(entry.value ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<OcppApiErrorInfo | null>(null);
  const [results, setResults] = useState<SetConfigResponse | null>(null);

  const onSave = async () => {
    setBusy(true);
    const res = await setConfig(chargerId, [{ key: entry.key, value }]);
    if (isOcppSuccess(res.status) && res.payload) {
      setResults(res.payload as SetConfigResponse);
      const accepted = (res.payload as SetConfigResponse).results?.[0]?.status;
      if (accepted === 'Accepted' || accepted === 'RebootRequired') {
        await new Promise((r) => setTimeout(r, 600));
        onSaved();
      }
    } else {
      setErr(mapOcppApiError(res.status, res.payload));
    }
    setBusy(false);
  };

  const lastStatus = results?.results?.[0]?.status as ConfigSetStatus | undefined;

  return (
    <SafeAreaView style={styles.modalSafe}>
      <View style={styles.modalHeader}>
        <Text style={styles.modalTitle}>Edit {entry.key}</Text>
        <Pressable onPress={onClose} hitSlop={12} accessibilityRole="button">
          <MaterialIcons name="close" size={26} color="#E5E7EB" />
        </Pressable>
      </View>
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        {err ? <OcppErrorBanner error={err} /> : null}
        <Text style={styles.fieldLabel}>Value</Text>
        <TextInput
          value={value}
          onChangeText={setValue}
          style={styles.input}
          autoCapitalize="none"
          autoCorrect={false}
          placeholderTextColor={OcppTheme.textDim}
        />
        {lastStatus ? (
          <View style={{ marginTop: 12 }}>
            <OcppStatusPill
              label={lastStatus}
              color={STATUS_COLOR[lastStatus] ?? STATUS_COLOR.Unknown}
            />
          </View>
        ) : null}
        <View style={styles.modalActions}>
          <OcppActionButton label="Cancel" variant="ghost" onPress={onClose} />
          <OcppActionButton label="Save" icon="save" onPress={onSave} loading={busy} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  driftRow: {
    backgroundColor: OcppTheme.surfaceAlt,
    padding: 10,
    borderRadius: 8,
  },
  driftKey: {
    color: OcppTheme.textPrimary,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  driftValue: {
    color: OcppTheme.textSecondary,
    fontSize: 12,
  },
  driftMono: {
    color: OcppTheme.warning,
    fontFamily: 'monospace',
  },
  cfgRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 12,
  },
  cfgKey: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '600',
  },
  cfgValue: {
    color: OcppTheme.textSecondary,
    fontSize: 12,
    marginTop: 2,
  },
  editBtn: {
    padding: 6,
  },
  historyRow: {
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  historyKey: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '600',
  },
  historyResult: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  historyValue: {
    color: OcppTheme.textSecondary,
    fontSize: 12,
    marginTop: 4,
  },
  historyMeta: {
    color: OcppTheme.textDim,
    fontSize: 11,
    marginTop: 2,
  },
  modalSafe: {
    flex: 1,
    backgroundColor: '#111418',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.border,
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: OcppTheme.textPrimary,
    marginRight: 12,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
  },
  fieldLabel: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginBottom: 4,
  },
  input: {
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: OcppTheme.textPrimary,
    fontSize: 14,
  },
});
